import { Button, Form, PageHeader, Trash } from '@lmc/ui-kit'
import { NAMESPACE } from 'locale'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import { useTranslation } from 'react-i18next'
import { Form as AntdForm } from 'antd'
import { useNavigate, useParams } from 'react-router-dom'
import { ReactNode, useEffect, useRef, useState } from 'react'

import {
  ButtonsWrapper,
  ProfileContent,
} from '../ResidentProfileScreen/styledComponents'
import { UpdateResidentReturnType } from 'hooks/useResident'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import styled from 'styled-components'
import {
  handleOnFinishFailed,
  scrollToTop,
} from '../ResidentProfileScreen/utils/utils'
import { showToast } from 'common/utils'
import { generalConstants } from 'locale/general'

type ImportantContactFormProps<T> = {
  children: ReactNode
  title: string
  updateResident: (values: T) => Promise<UpdateResidentReturnType>
  initialValues: Partial<T> | undefined
  onDelete: (() => void) | undefined
}

const ImportantContactForm = <T extends Record<string, any>>({
  children,
  title,
  updateResident,
  initialValues,
  onDelete,
}: ImportantContactFormProps<T>) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)

  const [form] = AntdForm.useForm()
  const navigate = useNavigate()
  const topRef = useRef<HTMLDivElement | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const { id: residentID } = useParams()

  useEffect(() => {
    scrollToTop(topRef)
  }, [])

  const handleOnFinish = async (values: T) => {
    try {
      setIsLoading(true)
      await updateResident(values)

      showToast({
        title: t(generalConstants.SUCCESS, { ns: NAMESPACE.GENERAL }),
        type: 'success',
      })

      navigate(`/residents/${residentID}/important-people`)
    } catch (err) {
      setIsLoading(false)
      showToast({
        title: t(generalConstants.ERROR, { ns: NAMESPACE.GENERAL }),
        type: 'error',
      })
    }
  }

  const handleOnCancel = () => {
    navigate(`/residents/${residentID}/important-people`)
  }

  return (
    <div ref={topRef}>
      <PageHeader sticky title={title}>
        <ButtonsWrapper>
          {onDelete && (
            <ConfirmationModal>
              <ButtonStyled onClick={onDelete} variant="danger">
                <Trash />
              </ButtonStyled>
            </ConfirmationModal>
          )}
          <Button onClick={handleOnCancel}>{t(supConstants.CANCEL)}</Button>
          <Button
            variant="primary"
            onClick={form.submit}
            type="submit"
            isLoading={isLoading}
          >
            {t(supConstants.SAVE)}
          </Button>
        </ButtonsWrapper>
      </PageHeader>
      <ProfileContent>
        <Form<T>
          onFinishFailed={({ errorFields }) =>
            handleOnFinishFailed(errorFields)
          }
          form={form}
          name="important-contact-form"
          onFinish={handleOnFinish}
          // onValuesChange={handleFormValuesChange}
          initialValues={initialValues}
        >
          {children}
        </Form>
      </ProfileContent>
    </div>
  )
}

const ButtonStyled = styled(Button)`
  height: 24px;
`

export default ImportantContactForm
